/**
 * Which master an action reads (1.2.0), as one control for the Export, Send to edit and QC dialogs, the batch dialogs
 * and the flow designer's inspectors.
 *
 *   masters given (one asset):  Default (what it means here spelled out: "Programme, closest to the preset: HD
 *   1080p25 · ProRes"), then the masters grouped by variant, each by format → { type: 'master', masterId }
 *   no masters (a selection, a workflow): Default, Primary master, then each variant (closest format) →
 *   { type: 'variant', variantId, format: null }; with `formats`, a variant with a fixed format class too.
 * value null is the default: Programme, the format closest to the output (the primary for Original format).
 * A chosen master that keeps its own timeline (Textless) says so below the select.
 */
import { AlertTriangle } from 'lucide-react';
import type { FormatClass, MasterChoiceOrAll, MasterDTO, MasterVariantDTO } from '@prism/contracts';
import { DEFAULT_MASTER_CHOICE, FORMAT_CLASSES, PROGRAMME_VARIANT_KEY, pickMaster, presetOutputSize } from '@prism/contracts';
import { useMasterVariants } from '../api/hooks';

export interface MasterPickerProps {
  /** One asset's masters (AssetDetail.masters); omitted for a selection or a workflow (variants only). */
  masters?: MasterDTO[];
  value: MasterChoiceOrAll | null;
  onChange: (value: MasterChoiceOrAll | null) => void;
  /** Copy to storage and Delete in workflows: offer "Every master" ({ type: 'all' }). */
  allowAll?: boolean;
  /** Offer a fixed format class with a variant (workflows: "Textless · UHD"). */
  formats?: boolean;
  /** The export preset (or null), to say which master "closest to the preset" means. */
  closestTo?: string | null;
  /** What the default means here, e.g. "Primary master" in workflows (default: "Programme, closest format"). */
  defaultLabel?: string;
  id?: string;
  disabled?: boolean;
  testId?: string;
}

export const encodeMasterChoice = (v: MasterChoiceOrAll | null): string =>
  !v ? '' : v.type === 'master' ? `m:${v.masterId}` : v.type === 'variant' ? `v:${v.variantId ?? ''}:${v.format ?? ''}` : v.type;

export function decodeMasterChoice(s: string): MasterChoiceOrAll | null {
  if (!s) return null;
  if (s === 'primary' || s === 'all') return { type: s };
  if (s.startsWith('m:')) return { type: 'master', masterId: s.slice(2) };
  const [, variantId, format] = s.split(':');
  return { type: 'variant', variantId: variantId || null, format: (format || null) as FormatClass | null };
}

/**
 * The master the default means for one asset: Programme closest to the preset; for Original format (or no preset
 * given) the primary when it is Programme, else the largest Programme master; falls back to the primary.
 */
export function defaultMasterOf(masters: readonly MasterDTO[], closestTo: string | null | undefined): MasterDTO | null {
  if (closestTo === 'original') return masters.find((m) => m.primary) ?? null;
  const size = closestTo ? presetOutputSize(closestTo) : null;
  return pickMaster(masters, DEFAULT_MASTER_CHOICE, size) ?? masters.find((m) => m.primary) ?? null;
}

/** The master a choice means for one asset (null for a variant it has no master of; the default when value is null). */
export function chosenMasterOf(masters: readonly MasterDTO[], value: MasterChoiceOrAll | null, closestTo: string | null | undefined): MasterDTO | null {
  if (!value) return defaultMasterOf(masters, closestTo);
  if (value.type === 'all') return null;
  const size = closestTo && closestTo !== 'original' ? presetOutputSize(closestTo) : null;
  return pickMaster(masters, value, size);
}

/** Masters grouped by variant in the list's order (unknown variants last), each group largest format first. */
export function groupMasters(masters: readonly MasterDTO[], variants: readonly Pick<MasterVariantDTO, 'id'>[]): Array<{ variant: MasterDTO['variant']; masters: MasterDTO[] }> {
  const order = (id: string) => { const i = variants.findIndex((v) => v.id === id); return i < 0 ? 1e6 : i; };
  const groups = new Map<string, { variant: MasterDTO['variant']; masters: MasterDTO[] }>();
  for (const m of masters) {
    const g = groups.get(m.variant.id) ?? { variant: m.variant, masters: [] };
    g.masters.push(m);
    groups.set(m.variant.id, g);
  }
  const cls = (m: MasterDTO) => FORMAT_CLASSES.indexOf(m.format.formatClass);
  return [...groups.values()]
    .sort((a, b) => order(a.variant.id) - order(b.variant.id) || (a.variant.key === PROGRAMME_VARIANT_KEY ? -1 : b.variant.key === PROGRAMME_VARIANT_KEY ? 1 : 0))
    .map((g) => ({ ...g, masters: [...g.masters].sort((a, b) => cls(a) - cls(b) || (a.primary === b.primary ? 0 : a.primary ? -1 : 1)) }));
}

export function MasterPicker({ masters, value, onChange, allowAll = false, formats = false, closestTo, defaultLabel, id, disabled, testId = 'master-picker' }: MasterPickerProps) {
  const variants = useMasterVariants().data?.items ?? [];
  const def = masters ? defaultMasterOf(masters, closestTo) : null;
  const chosen = masters ? chosenMasterOf(masters, value, closestTo) : null;
  const defaultText = defaultLabel
    ?? (masters
      ? closestTo === 'original'
        ? `Primary master${def ? `: ${def.label}` : ''}`
        : `Programme, ${closestTo ? 'closest to the format' : 'closest format'}${def ? `: ${def.label}` : ''}`
      : 'Programme, closest format');
  return (
    <span className="master-picker" data-testid={`${testId}-wrap`}>
      <select className="input" id={id} disabled={disabled} data-testid={testId} value={encodeMasterChoice(value)} onChange={(e) => onChange(decodeMasterChoice(e.target.value))}>
        <option value="">{defaultText}</option>
        {masters
          ? groupMasters(masters, variants).map((g) => (
              <optgroup key={g.variant.id} label={g.variant.name}>
                {g.masters.map((m) => <option key={m.id} value={`m:${m.id}`}>{m.label}{m.primary ? ' (primary)' : ''}</option>)}
              </optgroup>
            ))
          : <>
              <option value="primary">Primary master</option>
              <optgroup label="Variant, closest format">
                {variants.map((v) => <option key={v.id} value={`v:${v.id}:`}>{v.name}</option>)}
              </optgroup>
              {formats && variants.map((v) => (
                <optgroup key={`f:${v.id}`} label={`${v.name}, fixed format`}>
                  {FORMAT_CLASSES.map((c) => <option key={`${v.id}:${c}`} value={`v:${v.id}:${c}`}>{v.name} · {c}</option>)}
                </optgroup>
              ))}
            </>}
        {allowAll && <option value="all">Every master</option>}
      </select>
      {masters && value && value.type !== 'all' && !chosen && (
        <span className="help master-picker-note" data-testid={`${testId}-missing`}><AlertTriangle size={11} /> This asset has no master of that choice.</span>
      )}
      {chosen && !chosen.sharedTimeline && (
        <span className="help master-picker-note" data-testid={`${testId}-own-timeline`}>
          <AlertTriangle size={11} /> {chosen.variant.name} keeps its own timecode and length: the asset’s subtitles, markers and programme marks may not line up with it.
        </span>
      )}
    </span>
  );
}
